import * as Sentry from "@sentry/vue";
import { z } from "zod/v4";

import config from "@/config";
import { DEFAULT_PAGE_TITLE, SERVICE_CUSTOM } from "@/helpers/constants";
import { UTIL_COLORS } from "@/helpers/constants";
import { datePatterns } from "@/helpers/constants/dateTime";
import i18n from "@/i18n";

import type { WorkflowSnapshotStatus } from "@/models/applications";
import type {
  IFilePayload,
  Identifiable,
  MaybeArray,
  Nullish,
  Position2D,
  StipsCollection,
} from "@/models/common";
import type { IPaginatedResponse } from "@/models/common";
import type {
  IAttribute,
  IContextSetValueModel,
  ICustomAttributeContext,
  IDataService,
} from "@/models/orchestration";
import type { IScorecard, IScorecardGroup } from "@/models/scorecards";
import type { IUser } from "@/models/users";

export const handleSortByChange = (
  value: string,
  filters: Record<string, unknown>
) => (filters.sort_by = filters.sort_by !== value ? value : `-${value}`);

export const formatUsersForFilterDropdown = (
  data: IUser[],
  includeNoneOption = true
) => {
  return data.reduce<Record<string, string>>(
    (acc, curr) => {
      let name =
        curr?.full_name ||
        [curr?.first_name, curr?.last_name].filter(Boolean).join(" ") ||
        "";

      if (curr.deleted_at) {
        name += ` (${i18n.global.t("COMMON.DELETED")})`;
      }

      return { ...acc, [curr.id]: name };
    },
    // add option for no persons
    includeNoneOption ? { ["null"]: i18n.global.t("COMMON.NONE") } : {}
  );
};

// Reduces (IScorecard | IScorecardGroup)[] to Object { [id]: name }
export const formatScorecardsForFilterDropdown = (
  data: (IScorecard | IScorecardGroup)[],
  includeNoneOption = false
) => {
  return data.reduce<Record<string, string>>(
    (acc, curr) => {
      acc[curr.id] = curr.name;
      return acc;
    },
    includeNoneOption ? { ["null"]: i18n.global.t("COMMON.NONE") } : {}
  );
};

export const getWorkflowStatusFormatter = (
  property: "status" | "sub_status"
) => {
  return (data: WorkflowSnapshotStatus[]) => {
    return data.reduce<Record<string, string>>(
      (acc, curr) => {
        if (!curr[property]) {
          return acc;
        }

        acc[curr[property]] = curr[property];
        return acc;
      },
      { ["null"]: i18n.global.t("COMMON.NONE") }
    );
  };
};

export const getStipNameByType = (
  type: string,
  collections?: StipsCollection[]
) => {
  const category = collections
    ?.flatMap((c) => c.categories)
    .find((c) => c.type === type);
  return category ? category.name : "";
};

export const getDropdownOptions = (data: Identifiable[]) =>
  Object.values(data).reduce<Record<string, string>>((acc, curr) => {
    if (curr.id) {
      acc[curr.id] = curr.name;
    }
    return acc;
  }, {});

/* eslint-disable @typescript-eslint/no-explicit-any */
export const getDropdownOptionsByKey = <
  TData extends Record<string, any>,
  TKey extends keyof TData,
>(
  data: TData[],
  key: TKey
): Record<string, TData[TKey]> => {
  return data.reduce<Record<string, TData[TKey]>>((acc, curr) => {
    const keyValue = curr[key];
    if (keyValue !== undefined && keyValue !== null) {
      acc[String(keyValue)] = keyValue;
    }
    return acc;
  }, {});
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export const updatePageTitle = (title: string | Nullish) => {
  if (title) {
    document.title = `${title} | ${DEFAULT_PAGE_TITLE}`;
  }
};

export const getObjectKeys = <O extends object>(object: O) => {
  return Object.keys(object) as Array<keyof O>;
};

/**
 * Checking is it number.
 *
 * @param number | string
 * @returns {boolean}
 */
export const looksLikeNumber = (
  number: string | string[] | boolean | number | null | undefined
): boolean => {
  return !isNaN(Number(String(number)));
};

/**
 * Parses the boolean value to binary
 *
 * @param value The value to be parsed
 * @returns 1 | 0
 */
export function booleanToBinary(value: boolean | undefined): 1 | 0 {
  return value ? 1 : 0;
}

/**
 * Checking if it's a string.
 *
 * @param string | Nullish
 * @returns {string}
 */
export const validateString = (
  param: string | Nullish,
  emptyPlaceholder = "-"
) => {
  return typeof param === "string" ? param : emptyPlaceholder;
};

/**
 * Returns color based on the input.
 *
 * @param string
 * @returns {string}
 */
export const getColumnValueColor = (num: string, positive?: string) => {
  const numRegex = /[^0-9.-]+/g;
  return Number(num.replace(numRegex, "")) < 0
    ? UTIL_COLORS.ERROR
    : positive
      ? positive
      : "";
};

export const convertToArray = <T>(value: MaybeArray<T> | Nullish): T[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return isNullish(value) ? [] : [value];
};

export const isNullish = <T>(value: T | Nullish): value is null | undefined =>
  value === null || value === undefined;

export const stripHTMLTagsFromString = (
  str: string,
  noBreaks = true,
  breaksToNewlines = false
) => {
  if (breaksToNewlines) {
    // replace break tags and ending paragraph tags with newlines
    str = str.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, "\n");
    str = str.replace(/<br\s*\/?>/gi, "\n");
    str = str.replace(/<\/p>/gi, "\n");
    if (str.endsWith("\n")) {
      // remove any trailing newline
      str = str.slice(0, -1);
    }
  }
  if (!noBreaks) {
    // avoid issue where textContent removes spaces between tags
    str = str.replaceAll("</", " </");
  }
  const text = new DOMParser().parseFromString(str, "text/html");
  return validateString(text.body.textContent);
};

export const filterArrayFromNullish = <T>(valuesArray: (T | null)[]): T[] => {
  // due to runtime nature of filter, simple array.filter does not
  // infer the right type
  const result: T[] = [];

  valuesArray.forEach((value) => {
    if (!isNullish(value)) {
      result.push(value);
    }
  });

  return result;
};

export const getServiceAttributes = (acc: IAttribute[], curr: IDataService) => {
  if (curr.data_service_details) {
    const currentAttributes = Object.values(curr.data_service_details).reduce<
      IAttribute[]
    >((acc, curr) => acc.concat(Object.values(curr).flat()), []);

    acc.push(...currentAttributes);
    return acc;
  } else if (curr.attributes) {
    acc.push(...curr.attributes);
    return acc;
  }
  return acc;
};

export const getServiceProviderDetailsHelper = (
  serviceId: string,
  services: IDataService[]
) => {
  let serviceKey = -1;
  for (const service of services) {
    for (const [key, value] of Object.entries(
      service.data_service_details || []
    )) {
      for (const dataServices of Object.values(value)) {
        for (const srv of dataServices) {
          if (srv.id === serviceId) {
            serviceKey = Number(key);
            break;
          }
        }
      }
    }
  }

  let provider = null;
  for (const service of services) {
    for (const srv of service.services || []) {
      if (srv.id === serviceKey) {
        provider = srv;
        break;
      }
    }
  }
  return provider;
};

export const getServiceHelper = (
  serviceId: string,
  services: IAttribute[],
  context?: IContextSetValueModel
) => {
  return services.find((attr) => {
    if (
      serviceId === SERVICE_CUSTOM &&
      attr.context?.value &&
      !!context?.value
    ) {
      return (
        (attr.context.value as ICustomAttributeContext).id ===
        (context.value as ICustomAttributeContext).id
      );
    } else {
      return attr.id === serviceId;
    }
  });
};

export const getServiceByName = (
  name: IAttribute["name"],
  services: IAttribute[]
) => {
  return services.find((attr) => attr.name === name);
};

export const getIndividualName = <
  TIndividual extends { first_name?: string | null; last_name?: string | null },
>(
  individual: TIndividual,
  individualIndex: number
) => {
  if (individual.first_name || individual.last_name) {
    return [individual.first_name, individual.last_name]
      .filter(Boolean)
      .join(" ");
  }
  return `${i18n.global.t("COMMON.INDIVIDUAL")} ${individualIndex + 1}`;
};

/*
  Overcome lack of reverse mapping for string enum members
  https://www.typescriptlang.org/docs/handbook/enums.html#reverse-mappings
*/
export const getEnumKeyFromValue = <T extends { [key: string]: string }>(
  enumObject: T,
  value: string
): keyof T | null =>
  Object.keys(enumObject).find((enumKey) => enumObject[enumKey] === value) ||
  null;

export const createFormDataFromPayload = (
  payload: Record<string, unknown> | IFilePayload
) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (!value) {
      return;
    }
    if (typeof value === "object") {
      Object.entries(value).forEach(([sk, sv]) => {
        if (Array.isArray(sv)) {
          sv.forEach((item) => {
            formData.append(`${key}[${sk}][]`, item as string | Blob);
          });
        } else {
          formData.append(`${key}[${sk}]`, sv as string | Blob);
        }
      });
    } else {
      formData.append(key, value as string | Blob);
    }
  });
  return formData;
};

export const isValidDateString = <T>(str: T) => {
  if (typeof str !== "string" || !str.trim()) {
    return false;
  }

  const matchesPattern = datePatterns.some((pattern) =>
    pattern.test(str.trim())
  );

  const date = new Date(str);
  const isValidDate =
    !isNaN(date.getTime()) && date.toString() !== "Invalid Date";

  return matchesPattern && isValidDate;
};

/**
 * Safely validates the data against the schema and logs the errors if any
 * @param schema The schema to validate the data with
 * @param data The data to validate
 * @returns The validated data or the original data if validation fails
 */
export function validateWithSchema<TSchema extends z.ZodSchema, TData>(
  schema: TSchema,
  data: TData
) {
  const schemaResult = schema.safeParse(data);

  if (schemaResult.success) {
    return schemaResult.data;
  }

  if (config.env === "production") {
    Sentry.captureException(schemaResult.error);
  } else {
    console.error("Schema validation failed", schemaResult.error);
  }

  return data;
}

/**
 * Safely validates paginated data against the schema and logs the errors if any
 * @param schema The schema to validate the data with
 * @param data The paginated response to validate
 * @returns The validated data or the original data if validation fails
 */
export function validatePaginatedResponseWithSchema<
  TSchema extends z.ZodSchema,
  TData,
>(schema: TSchema, paginatedResponse: IPaginatedResponse<TData>) {
  validateWithSchema(z.array(schema), paginatedResponse.data);
  return paginatedResponse;
}

/**
 * Strips away any special characters, whitespaces and numbers from the passed string
 * @param string
 */
export const extractLetters = (string: string) =>
  string.replace(/[^a-zA-Z]/g, "");

export const stringCompare = (a: string, b: string) => {
  if (a === "None") {
    return -1;
  }
  if (b === "None") {
    return 1;
  }
  return a.localeCompare(b);
};

export const areShallowEqual = <T extends Record<string, unknown>>(
  obj1: T,
  obj2: T
): boolean => {
  if (obj1 === obj2) {
    return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(obj2, key) && obj1[key] === obj2[key]
  );
};

/**
 * Returns a 2D empty position object with x and y set to 0.
 */
export const get2dEmptyPosition = (): Position2D => ({
  x: 0,
  y: 0,
});

export const sleep = (timeMs: number) =>
  new Promise((resolve) => setTimeout(resolve, timeMs));

/**
 * Creates an array of unique values that are included in all given arrays.
 * The order and references of result values are determined by `originArray`.
 *
 * @param originArray The array to inspect.
 * @param comparisonArray Array of values to compare the origin array against.
 * @param callback The callback to be invoked per element.
 * @returns Returns a new array of intersecting values.
 */
export function intersectionBy<T>(
  originArray: T[],
  comparisonArray: T[],
  callback: (value: T) => unknown
): T[] {
  if (!originArray.length || !comparisonArray.length) {
    return [];
  }

  const set = new Set(comparisonArray.map(callback));
  return originArray.filter((item) => set.has(callback(item)));
}

/**
 * Maps and filters an array of items based on the provided callbacks.
 * Uses Array.prototype.reduce to apply the map and filter in a single pass.
 */
export const filterMap = <T, U>(
  array: T[],
  filterFn: (item: T, index: number, array: T[]) => boolean,
  mapFn: (item: T, index: number, array: T[]) => U
): U[] => {
  return array.reduce<U[]>((acc, item, index, arr) => {
    if (filterFn(item, index, arr)) {
      acc.push(mapFn(item, index, arr));
    }
    return acc;
  }, []);
};
